import { useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertDialogActions,
  AlertDialogBody,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  useAlertDialog,
} from '@clay-run/glaze/alert-dialog';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import type { IconType } from '@clay-run/glaze/icon';
import { Plus, TextAa, TrashSimple } from '@phosphor-icons/react';
import { Text } from '@clay-run/glaze/text';
import { addErrorToast, addToast } from '@clay-run/glaze/toast/toast-queue';

import { useApi } from '../../_shared/hooks';
import type { Campaign } from '../../_shared/launcher-campaign.types';
import { getPlays } from '../api-client';
import { archivePlay, renamePlay } from '../mutations';
import {
  getPlayRowMenuItems,
  playDeleteTitle,
  playDeleteWarning,
  playDisplayName,
} from '../plays-logic';
import type { PlaybooksConfigRowAction } from '../config-page-logic';
import { playsQueryKey } from '../query-keys';
import type { PlaybooksPlaySummary } from '../schemas';
import { invalidatePlayLists } from '../use-plays-library';
import { KebabMenu } from './KebabMenu';
import type { KebabMenuItem } from './KebabMenu';
import { NameDrawerField } from './NameDrawerField';
import { PushDrawer } from './PushDrawer';

/**
 * The play flow's OWN campaign-scoped plays read (QA round 7, U1, item 1 —
 * rewritten). `campaign.id`/`campaignSfdcId` IS the campaign's SFDC id
 * (`launcher_sfdc_campaigns.id`, verified against `mini-apps/launcher/api.ts`'s
 * `createListCampaigns` — the column aliased `event_date` is the only rename;
 * `id` passes through as the SFDC id unchanged), so it's exactly what
 * `/v2/plays?campaignSfdcId=…` and `mutations.createPlay` expect.
 *
 * Since QA round 7 this is used ONLY by `PlayFlowShell` (one request per play
 * OPEN, to resolve which play the URL's `:playId` names and to build the
 * play's heading) — Home and the Calendar no longer call it at all. The
 * per-visible-row fan-out this doc used to describe is gone: those two list
 * surfaces read the batch `GET /plays/library` once via `usePlaysLibrary`
 * (`use-plays-library.ts`) instead. The paragraph that used to rule out a
 * bulk endpoint here is stale for the same reason — `GET /plays/library`
 * IS that bulk endpoint, and the list surfaces already use it; this
 * campaign-scoped read stays because the play flow genuinely only needs one
 * campaign's plays at a time, cache-shared across the shell's tabs.
 */
export function usePlaysForCampaign(campaignSfdcId: string, opts?: { enabled?: boolean }) {
  const { apiFetch } = useApi();
  return useQuery({
    queryKey: playsQueryKey(campaignSfdcId),
    queryFn: () => getPlays(apiFetch, { campaignSfdcId }),
    staleTime: Infinity,
    enabled: opts?.enabled ?? true,
  });
}

export interface PlayRowActions {
  menuItemsFor: (play: PlaybooksPlaySummary, displayName: string) => KebabMenuItem[];
  /** Render once per host — the rename drawer for whichever play is being renamed, or `null`. */
  renameDrawer: ReactNode;
}

/**
 * Rename + Delete for ONE play row, shared by Home's per-campaign list
 * (`PlaysActions`), the Library's "Plays" section (`PlaysLibrarySection`) and
 * the play header's own kebab (`PlayFlowShell`). `afterChange` is the host's
 * cache reconciliation — always `invalidatePlayLists(queryClient, play.campaignSfdcId)`
 * today, taken as a callback so a host can add its own patch-in-place first.
 * Behaviour is byte-identical to the pre-extraction `PlaysActions` code:
 * the drawer stays open on a failed rename, the delete dialog stays open on a
 * failed archive, both toast the same sentences.
 */
export function usePlayRowActions(afterChange: (play: PlaybooksPlaySummary) => Promise<void>): PlayRowActions {
  const { apiFetch } = useApi();
  const { openAlertDialog, closeAlertDialog } = useAlertDialog();
  /**
   * Playbooks QA round 4, U10 (P2) — which play's Rename drawer is open, and
   * what it is currently called (the drawer seeds `NameDrawerField` once per
   * mount, so it needs the display name up front). `null` = closed.
   */
  const [renameTarget, setRenameTarget] = useState<{ id: string; displayName: string } | null>(null);
  const [savingRename, setSavingRename] = useState(false);

  async function handleRenameSave(name: string): Promise<void> {
    if (!renameTarget) return;
    setSavingRename(true);
    try {
      const renamed = await renamePlay(apiFetch, renameTarget.id, name);
      await afterChange(renamed);
      addToast({ content: `Renamed to "${name}".`, status: 'success' });
      setRenameTarget(null);
    } catch (err) {
      // The drawer STAYS open on failure — the rep still needs the field they
      // just typed into, and closing it would discard the name silently.
      addErrorToast({
        content: err instanceof Error ? err.message : 'Could not rename this play — try again.',
      });
    } finally {
      setSavingRename(false);
    }
  }

  /**
   * U10 (P1) — the delete confirm. ALWAYS warns (Decision 9); the copy's
   * strength comes from `playDeleteWarning(sendCount)`, and `sendCount` rides
   * on the summary this row already has (no extra fetch).
   */
  function confirmDelete(play: PlaybooksPlaySummary, displayName: string): void {
    openAlertDialog({
      size: 'sm',
      content: (
        <>
          <AlertDialogHeader onClose={closeAlertDialog}>
            <AlertDialogTitle>{playDeleteTitle(displayName)}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody className="flex flex-col gap-3">
            <AlertDialogDescription>{playDeleteWarning(play.sendCount)}</AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogActions>
            <Button variant="secondary" onClick={closeAlertDialog}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                archivePlay(apiFetch, play.id)
                  .then(async () => {
                    await afterChange(play);
                    closeAlertDialog();
                    addToast({ content: `Deleted "${displayName}".`, status: 'success' });
                  })
                  .catch((err: unknown) => {
                    // Dialog stays open on failure (same shape as Config's own
                    // playbook delete): the rep can retry or cancel.
                    addErrorToast({
                      content:
                        err instanceof Error ? err.message : 'Could not delete this play — try again.',
                    });
                  });
              }}
            >
              Delete
            </Button>
          </AlertDialogActions>
        </>
      ),
    });
  }

  function menuItemsFor(play: PlaybooksPlaySummary, displayName: string): KebabMenuItem[] {
    const iconByAction: Partial<Record<PlaybooksConfigRowAction, IconType>> = {
      rename: TextAa,
      delete: TrashSimple,
    };
    const onSelectByAction: Partial<Record<PlaybooksConfigRowAction, () => void>> = {
      rename: () => setRenameTarget({ id: play.id, displayName }),
      delete: () => confirmDelete(play, displayName),
    };
    return getPlayRowMenuItems().map((descriptor) => ({
      key: descriptor.action,
      label: descriptor.label,
      icon: iconByAction[descriptor.action],
      danger: descriptor.danger,
      onSelect: onSelectByAction[descriptor.action] ?? (() => {}),
    }));
  }

  const renameDrawer = renameTarget ? (
    <PushDrawer title={`Rename ${renameTarget.displayName}`} onClose={() => setRenameTarget(null)}>
      {/*
        Seeded with the DISPLAY name, not `play.name` — a rep renaming an
        unnamed play starts from the playbook name it currently shows,
        which is what they see on the row. `key`ed so switching targets
        reseeds (the field seeds once per mount).
      */}
      <NameDrawerField
        key={renameTarget.id}
        initialName={renameTarget.displayName}
        saving={savingRename}
        onSave={(name) => void handleRenameSave(name)}
        onCancel={() => setRenameTarget(null)}
      />
    </PushDrawer>
  ) : null;

  return { menuItemsFor, renameDrawer };
}

export interface PlaysActionsProps {
  plays: PlaybooksPlaySummary[];
  campaign: Campaign;
  playbookNameById: Map<string, string>;
  basePath: string;
  onCreatePlay: (campaign: Campaign) => void;
  /** True while `CreatePlaySidebar` is open for this campaign — disables (rather than spinners) the button, since the in-flight submit spinner now lives on the sidebar's own Create button. */
  creatingPlay: boolean;
  /**
   * QA round 7, U1 (items 2, 9) — the row's "Open" navigation, supplied by
   * the host so it goes through that host's guard: this component holds NO
   * router hook of its own. An anchor without `data-playbooks-nav` can never
   * be seen by `UnsavedWorkGuard`'s capture-phase click listener, so "Open"
   * is a `Button` + callback here, never a `ButtonLink`/anchor.
   */
  onOpen: (href: string) => void;
}

/**
 * Per-play "Open" links + the always-available "Create Play" button, shared
 * by `PlaybooksHome.tsx`'s `CampaignPlaysSection` (List view) and
 * `CalendarMonthGrid.tsx`'s chip popover (R13) so the two surfaces render
 * identical actions instead of duplicating this JSX. Lives in its own module
 * (rather than being defined in either consumer) so neither surface imports
 * the other — `CalendarMonthGrid` importing this from `PlaybooksHome`
 * directly would create a circular module dependency, since
 * `PlaybooksHome` already imports `CalendarMonthGrid`.
 */
export function PlaysActions({
  plays,
  campaign,
  playbookNameById,
  basePath,
  onCreatePlay,
  creatingPlay,
  onOpen,
}: PlaysActionsProps) {
  const queryClient = useQueryClient();
  const actions = usePlayRowActions((play) => invalidatePlayLists(queryClient, play.campaignSfdcId));

  return (
    <div className="flex flex-col gap-1.5">
      {plays.map((play) => {
        // U10 (P2) — `play.name` when the rep named it, else the playbook's
        // name, else the raw id. One helper, shared with `PlayFlowShell`'s
        // heading, so the list and the flow can never disagree.
        const displayName = playDisplayName(play, playbookNameById);
        return (
        <div key={play.id} className="flex items-center gap-2 flex-wrap">
          <Text size="sm" weight="medium" className="min-w-0 truncate">
            {displayName}
          </Text>
          {/*
            U5 discovery: A1 exposes no `GET /v2/plays/:id` — only the
            campaign-scoped list this component already calls — so
            `PlayFlowShell` (mounted at `v2/play/:playId`) has no way to
            resolve which campaign a play belongs to from the id alone.
            Carrying `campaignSfdcId` on the URL (the same "route state via
            query param" idiom U5's own `?step=` uses) is the client-only
            fix; it costs no schema change and needs no round-trip to A1.
          */}
          <span className="ml-auto flex shrink-0 items-center gap-1">
            {/*
              QA round 7, item 9 — `primary`, which Terra's tokens render blue
              (`index.css` maps `--color-bg-action-inverse` to
              `blueberry-500`); item 2 — a `Button` + host callback, never an
              anchor: an anchor without `data-playbooks-nav` can't be seen by
              the guard, and Glaze's `ButtonLink` only works in the real app
              because `vite.config.ts` dedupes react-router-dom (vitest
              doesn't).
            */}
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                onOpen(`${basePath}/play/${play.id}?campaignSfdcId=${encodeURIComponent(campaign.id)}`)
              }
            >
              Open
            </Button>
            {/* U10 (P1/P2) — Rename / Delete. Not admin-gated (Decision 7). */}
            <KebabMenu
              items={actions.menuItemsFor(play, displayName)}
              ariaLabel={`More actions for ${displayName}`}
            />
          </span>
        </div>
        );
      })}
      <div className="flex justify-end">
        <Button
          variant={plays.length === 0 ? 'secondary' : 'ghost'}
          size="sm"
          disabled={creatingPlay}
          onClick={() => onCreatePlay(campaign)}
        >
          <Icon type={Plus} /> Create Play
        </Button>
      </div>
      {actions.renameDrawer}
    </div>
  );
}
